/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-09
 */

import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  showSuccessNotification,
  hideSuccessNotification,
  clearAllSuccessNotifications,
  selectSuccessNotifications,
  selectLatestSuccessNotification,
} from '../features/notifications';
import { SuccessType } from '../components/common/SuccessAlert';
import type { AppDispatch, RootState } from '../store';

// Success notification options interface
interface ShowSuccessOptions {
  message: string;
  successType?: SuccessType;
  showAction?: boolean;
  actionLabel?: string;
  autoHide?: boolean;
  hideDelay?: number;
}

/**
 * Custom hook for managing success notifications
 * Provides easy-to-use functions for showing and managing success messages
 *
 * @example
 * const { showSuccess, hideSuccess, clearAll } = useSuccessNotification();
 *
 * // Show a simple success message
 * showSuccess({ message: "Profile updated successfully!", successType: "profile" });
 *
 * // Show success with action button
 * showSuccess({
 *   message: "Registration complete!",
 *   successType: "registration",
 *   showAction: true,
 *   actionLabel: "Continue to Dashboard"
 * });
 *
 * @returns {object} Success notification management functions and state
 */
export const useSuccessNotification = () => {
  const dispatch = useDispatch<AppDispatch>();
  const successNotifications = useSelector((state: RootState) =>
    selectSuccessNotifications(state)
  );
  const latestNotification = useSelector((state: RootState) =>
    selectLatestSuccessNotification(state)
  );

  /**
   * Show a success notification
   */
  const showSuccess = useCallback(
    (options: ShowSuccessOptions) => {
      dispatch(
        showSuccessNotification({
          ...options,
          successType: options.successType || 'general',
        })
      );
    },
    [dispatch]
  );

  /**
   * Hide a specific success notification by ID
   */
  const hideSuccess = useCallback(
    (id: string) => {
      dispatch(hideSuccessNotification(id));
    },
    [dispatch]
  );

  /**
   * Clear all success notifications
   */
  const clearAll = useCallback(() => {
    dispatch(clearAllSuccessNotifications());
  }, [dispatch]);

  // Helper functions for common success scenarios
  const showRegistrationSuccess = useCallback(
    (
      message = 'Registration successful! Please check your email to verify your account.'
    ) => {
      showSuccess({
        message,
        successType: 'registration',
        autoHide: false, // Keep visible for important registration message
      });
    },
    [showSuccess]
  );

  const showLoginSuccess = useCallback(
    (userName?: string) => {
      const message = userName ? `Welcome back, ${userName}!` : 'Welcome back!';
      showSuccess({
        message,
        successType: 'login',
        hideDelay: 3000,
      });
    },
    [showSuccess]
  );

  const showLogoutSuccess = useCallback(
    (message = 'You have been logged out successfully.') => {
      showSuccess({
        message,
        successType: 'logout',
        hideDelay: 3000,
      });
    },
    [showSuccess]
  );

  const showProfileUpdateSuccess = useCallback(
    (message = 'Your profile has been updated successfully.') => {
      showSuccess({
        message,
        successType: 'profile',
        hideDelay: 4000,
      });
    },
    [showSuccess]
  );

  const showPasswordChangeSuccess = useCallback(
    (message = 'Your password has been changed successfully.') => {
      showSuccess({
        message,
        successType: 'password',
        hideDelay: 4000,
      });
    },
    [showSuccess]
  );

  const showVerificationSuccess = useCallback(
    (message = 'Email verification completed successfully!') => {
      showSuccess({
        message,
        successType: 'verification',
        hideDelay: 5000,
      });
    },
    [showSuccess]
  );

  const showPasswordResetEmailSent = useCallback(
    (message = 'Password reset email sent! Please check your inbox.') => {
      showSuccess({
        message,
        successType: 'general',
        autoHide: false, // Keep visible for important message
      });
    },
    [showSuccess]
  );

  const showPasswordResetSuccess = useCallback(
    (
      message = 'Password reset successful! You can now log in with your new password.'
    ) => {
      showSuccess({
        message,
        successType: 'password',
        showAction: true,
        actionLabel: 'Go to Login',
        autoHide: false,
      });
    },
    [showSuccess]
  );

  return {
    // Core functions
    showSuccess,
    hideSuccess,
    clearAll,

    // State
    successNotifications,
    latestNotification,
    hasNotifications: successNotifications.length > 0,

    // Helper functions for common scenarios
    showRegistrationSuccess,
    showLoginSuccess,
    showLogoutSuccess,
    showProfileUpdateSuccess,
    showPasswordChangeSuccess,
    showVerificationSuccess,
    showPasswordResetEmailSent,
    showPasswordResetSuccess,
  };
};

export default useSuccessNotification;
